import config from '../config';

class ApiService {
  constructor() {
    this.baseURL = config.API_BASE_URL;
    this.weeklyReportsBase = `${this.baseURL}${config.WEEKLY_REPORTS_BASE}`;
  }

  // Generic request handler
  async request(endpoint, options = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`;
    
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, { ...defaultOptions, ...options });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Handle different content types
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else if (contentType && contentType.includes('text/')) {
        return await response.text();
      } else {
        return await response.blob();
      }
    } catch (error) {
      console.error(`API request failed for ${url}:`, error);
      throw error;
    }
  }

  // Batch Management
  async getBatches() {
    return this.request(`${config.WEEKLY_REPORTS_BASE}/batches`);
  }

  async getLatestBatch() {
    return this.request(`${config.WEEKLY_REPORTS_BASE}/latest-batch`);
  }

  // Executive Summary
  async getBatchSummary(batchId) {
    return this.request(`${config.WEEKLY_REPORTS_BASE}/${batchId}/summary`);
  }

  // Mutations Analysis
  async getNovelMutations(batchId) {
    return this.request(`${config.WEEKLY_REPORTS_BASE}/${batchId}/novel-mutations`);
  }

  async getKnownMutations(batchId) {
    return this.request(`${config.WEEKLY_REPORTS_BASE}/${batchId}/known-mutations`);
  }

  // Chart Data
  async getChartData(batchId) {
    return this.request(`${config.WEEKLY_REPORTS_BASE}/${batchId}/charts`);
  }

  // Temporal Analysis
  async getTemporalAnalysis(batchId) {
    return this.request(`${config.WEEKLY_REPORTS_BASE}/${batchId}/temporal-analysis`);
  }

  // Real-time Alerts
  async getRealTimeAlerts(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`${config.WEEKLY_REPORTS_BASE}/alerts/real-time?${queryString}`);
  }

  // Investigation Queue
  async getInvestigationQueue(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`${config.WEEKLY_REPORTS_BASE}/alerts/investigation-queue?${queryString}`);
  }

  // Set Alert Thresholds
  async setAlertThresholds(thresholds) {
    return this.request(`${config.WEEKLY_REPORTS_BASE}/alerts/thresholds`, {
      method: 'POST',
      body: JSON.stringify(thresholds),
    });
  }

  // Advanced Analytics - Genomic Surveillance
  async getMutationHotspots(batchId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`${config.WEEKLY_REPORTS_BASE}/${batchId}/surveillance/hotspots?${queryString}`);
  }

  async getEmergencePatterns(batchId) {
    return this.request(`${config.WEEKLY_REPORTS_BASE}/${batchId}/surveillance/emergence-patterns`);
  }

  async getResistanceMarkers(batchId) {
    return this.request(`${config.WEEKLY_REPORTS_BASE}/${batchId}/surveillance/resistance-markers`);
  }

  // Advanced Analytics - Mutations
  async getMutationCoOccurrence(batchId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`${config.WEEKLY_REPORTS_BASE}/${batchId}/mutations/co-occurrence?${queryString}`);
  }

  async getTemporalMutationDynamics(batchId = null, params = {}) {
    const queryParams = {};

    // If batchId is provided, query specific batch
    // Otherwise query by date range (multi-batch temporal analysis)
    if (batchId) {
      queryParams.batch_id = batchId;
      console.log(`[API] getTemporalMutationDynamics: Single batch query for batch_id=${batchId}`);
    } else {
      // Multi-batch query - use days_back parameter (default 30 days)
      queryParams.days_back = params.days_back || 30;
      console.log(`[API] getTemporalMutationDynamics: Multi-batch query with days_back=${queryParams.days_back}`);
    }

    // Add optional filters
    if (params.mutation_name) queryParams.mutation_name = params.mutation_name;
    if (params.granularity) queryParams.granularity = params.granularity;

    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${config.WEEKLY_REPORTS_BASE}/mutations/temporal-dynamics?${queryString}`;
    console.log(`[API] Final URL for temporal dynamics:`, url);
    return this.request(url);
  }

  /**
   * Get temporal mutation dynamics across multiple batches (time-series)
   * @param {Object} params - { days_back, mutation_name, granularity }
   * @returns {Promise<Object>} Temporal dynamics data across multiple periods
   */
  async getMultiBatchTemporalDynamics(params = {}) {
    return this.getTemporalMutationDynamics(null, params);
  }

  // Predictive Analytics
  async getPandemicRiskScores(batchId) {
    return this.request(`${config.WEEKLY_REPORTS_BASE}/${batchId}/prediction/pandemic-risk`);
  }

  // Laboratory Integration - NEW API ENDPOINTS
  async getPrimerDesign(batchId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/lab/primers/${batchId}?${queryString}` : `/lab/primers/${batchId}`;
    return this.request(url);
  }

  async getDiagnosticTargets(batchId) {
    return this.request(`/lab/diagnostic-targets/${batchId}`);
  }

  async getAntiviralSusceptibility(batchId) {
    return this.request(`/lab/antiviral-susceptibility/${batchId}`);
  }

  // Comparative Genomics
  async getStrainDifferences(batchId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`${config.WEEKLY_REPORTS_BASE}/${batchId}/comparative/strain-differences?${queryString}`);
  }

  async getHostAdaptation(batchId) {
    return this.request(`${config.WEEKLY_REPORTS_BASE}/${batchId}/comparative/host-adaptation`);
  }

  // New Analytics Endpoints
  async searchSequences(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/weekly-reports/analytics/sequences/search?${queryString}`);
  }

  async getSequenceDetails(sequenceId) {
    return this.request(`/weekly-reports/analytics/sequences/${sequenceId}`);
  }

  async getGeographySummary(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/analytics/geography/summary?${queryString}`);
  }

  async getGeographyTimeseries(country, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/analytics/geography/${country}/timeseries?${queryString}`);
  }

  async getMutationTrend(mutationName, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/analytics/mutations/${mutationName}/trend?${queryString}`);
  }

  async compareBatches(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`${config.WEEKLY_REPORTS_BASE}/compare/batches?${queryString}`);
  }

  async getPipelineHealth(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/weekly-reports/analytics/pipeline/health${qs ? `?${qs}` : ''}`);
  }

  async getPipelineOutliers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/analytics/pipeline/outliers?${queryString}`);
  }

  async getDailyDigest(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/analytics/digest/daily?${queryString}`);
  }

  // Export
  async exportAnalysis(batchId, format = 'json') {
    const response = await this.request(`${config.WEEKLY_REPORTS_BASE}/${batchId}/export?format=${format}`);
    return response;
  }

  async exportAnalyticsData(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/analytics/export/analysis?${queryString}`);
  }

  // ===================================================================
  // HA-ONLY RISK ASSESSMENT API METHODS
  // ===================================================================

  /**
   * Get HA-only risk assessment for a single isolate
   * @param {string} isolateId - Isolate ID
   * @returns {Promise<Object>} HA risk assessment data
   */
  async getHARiskForIsolate(isolateId) {
    return this.request(`/ha-risk/isolate/${isolateId}`);
  }

  /**
   * Get HA-only risk assessments for all isolates in a batch
   * @param {string} batchId - Batch ID
   * @param {Object} params - { limit, offset }
   * @returns {Promise<Object>} Batch HA risk assessment data
   */
  async getHARiskForBatch(batchId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/ha-risk/batch/${batchId}?${queryString}`);
  }

  /**
   * Get HA-only risk assessments for all isolates (paginated)
   * @param {Object} params - { limit, offset, min_risk_score }
   * @returns {Promise<Object>} All isolates HA risk data
   */
  async getAllHARiskAssessments(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/ha-risk/all-isolates?${queryString}`);
  }

  /**
   * Get list of HA pandemic markers being tracked
   * @returns {Promise<Object>} HA pandemic markers
   */
  async getHAPandemicMarkers() {
    return this.request('/ha-risk/markers');
  }

  // ===================================================================
  // DASHBOARD ANALYSES API METHODS (Pre-calculated HA Component Scores)
  // ===================================================================

  /**
   * Get all sequences with HA component scores from dashboard_analyses collection
   * @param {Object} params - { limit, offset, min_score }
   * @returns {Promise<Object>} Sequences with pre-calculated HA component scores
   */
  async getDashboardAnalyses(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/dashboard-analyses?${queryString}`);
  }

  /**
   * Get detailed HA analysis for a specific sequence
   * @param {string} sequenceId - Sequence ID
   * @returns {Promise<Object>} Complete HA component analysis for sequence
   */
  async getDashboardSequence(sequenceId) {
    return this.request(`/dashboard-analyses/sequence/${sequenceId}`);
  }

  /**
   * Get overall statistics for HA components
   * @returns {Promise<Object>} Aggregated statistics for all HA components
   */
  async getDashboardStatistics() {
    return this.request('/dashboard-analyses/statistics');
  }

  /**
   * Get high-risk sequences (overall_ha_score >= 60 or critical mutations)
   * @param {Object} params - { limit }
   * @returns {Promise<Object>} High-risk sequences
   */
  async getHighRiskSequences(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/dashboard-analyses/high-risk?${queryString}`);
  }

  // ===================================================================
  // SCIENTIFIC HUMAN ADAPTATION ANALYSIS API METHODS
  // (Using output_analysis_results with WHO TIPRA framework)
  // ===================================================================

  /**
   * Get aggregate human adaptation statistics with scientific mutation analysis
   * Returns: Critical mutation counts (Q226L, G228S, etc.) with literature references
   * @returns {Promise<Object>} Scientific mutation analysis for all sequences
   */
  async getHumanAdaptationAnalysis() {
    return this.request('/human-adaptation-analysis');
  }

  // Get sequences containing a specific mutation
  async getSequencesByMutation(mutationName) {
    return this.request(`/sequences-by-mutation/${mutationName}`);
  }

  /**
   * Get detailed scientific mutation analysis for a specific sequence
   * Returns: All mutations with literature refs, receptor preference, risk assessment
   * @param {string} sequenceId - Sequence ID (e.g., EPI_ISL_19737058)
   * @returns {Promise<Object>} Complete scientific mutation analysis
   */
  async getReceptorBindingAnalysis(sequenceId) {
    return this.request(`/human-adaptation-analysis/${sequenceId}`);
  }
}

// Create singleton instance
const apiService = new ApiService();

export default apiService;